import React, { useState, useMemo, useEffect } from 'react';
import {
  CreditCard, Search, CheckCircle, Clock,
  AlertCircle, DollarSign, CornerUpLeft, ChevronDown, ChevronUp, Eye,
  Plus, X, FileText, FileCheck, ClipboardList, Scale
} from 'lucide-react';

// 状态颜色映射
const STATUS_COLORS = {
  '待审批': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  '审批中': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  '待付款': { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  '已付款': { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
};

// 状态徽章组件
const StatusBadge = ({ status }) => {
  const config = STATUS_COLORS[status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
       {status}
    </span>
  );
};

// 从送货单数据提取付款单 - 按付款单号聚合，合并单只显示一行
const extractPaymentDocuments = (deliveries) => {
  const groups = {};

  deliveries.forEach(delivery => {
    // 付款单 - 状态：待审批、待付款、已付款
    if (delivery.payment) {
      const paymentNo = delivery.payment.no;

      if (!groups[paymentNo]) {
        let paymentStatus = '已付款';
        if (delivery.payment.pendingApproval) {
          paymentStatus = '待审批';
        } else if (delivery.payment.pendingPay) {
          paymentStatus = '待付款';
        }

        groups[paymentNo] = {
          id: paymentNo,
          docNo: paymentNo,
          orderIds: [],
          orderNos: [],
          deliveryIds: [],
          deliveryNos: [],
          supplierId: delivery.supplierId,
          supplierName: delivery.supplierName,
          baseId: delivery.baseId,
          baseName: delivery.baseName,
          materialName: delivery.materialName,
          amount: 0,
          paymentTime: delivery.payment.at,
          paymentMethod: delivery.payment.method,
          transactionNo: delivery.payment.transactionNo,
          bankName: delivery.payment.bankName,
          bankAccount: delivery.payment.bankAccount,
          applyDate: delivery.payment.applyDate,
          requiredAmount: 0,
          paidAmount: 0,
          voucher: delivery.payment.voucher,
          status: paymentStatus,
          createdAt: delivery.payment.at,
          deliveries: [],
          invoiceNos: [],
          settlementNos: [],
        };
      }

      // 累加数据
      const group = groups[paymentNo];
      if (!group.orderIds.includes(delivery.orderId)) {
        group.orderIds.push(delivery.orderId);
        group.orderNos.push(delivery.orderNo);
      }
      group.deliveryIds.push(delivery.id);
      group.deliveryNos.push(delivery.deliveryNo);
      group.deliveries.push(delivery);

      // 获取发票单号
      const invoiceNo = delivery.invoice?.no;
      if (invoiceNo && !group.invoiceNos.includes(invoiceNo)) {
        group.invoiceNos.push(invoiceNo);
      }

      // 获取结算单号
      const settlementNo = delivery.settlement?.settlementNo;
      if (settlementNo && !group.settlementNos.includes(settlementNo)) {
        group.settlementNos.push(settlementNo);
      }

      // 累加金额
      group.amount += delivery.payment.amount || 0;
      group.requiredAmount += delivery.payment.requiredAmount || delivery.payment.amount || 0;
      group.paidAmount += delivery.payment.paidAmount || 0;

      // 更新付款时间（取最早的）
      const paymentTime = delivery.payment.at;
      if (paymentTime && paymentTime < group.paymentTime) {
        group.paymentTime = paymentTime;
        group.createdAt = paymentTime;
      }
    }
  });

  // 转换为数组并标记是否为合并单
  return Object.values(groups).map(group => ({
    ...group,
    amount: Math.round(group.amount * 100) / 100,
    requiredAmount: Math.round(group.requiredAmount * 100) / 100,
    paidAmount: Math.round(group.paidAmount * 100) / 100,
    isMerged: group.invoiceNos.length >= 2,
  }));
};

const PaymentView = ({ deliveries = [], onPaymentClick, onWithdraw, onPay, onInvoiceClick, initialFilter, setInitialFilter }) => {
  // 提取付款单数据
  const payments = useMemo(() => extractPaymentDocuments(deliveries), [deliveries]);

  // 搜索状态
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status) {
        setStatusFilter(initialFilter.status);
      }
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 新增付款单弹窗状态
  const [showAddModal, setShowAddModal] = useState(false);

  // 获取供应商列表
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(payments.map(p => p.supplierName).filter(Boolean))];
    return uniqueSuppliers.sort();
  }, [payments]);

  // 发票展开状态
  const [expandedInvoiceIds, setExpandedInvoiceIds] = useState([]);

  // 切换发票展开状态
  const toggleInvoiceExpand = (paymentId) => {
    setExpandedInvoiceIds(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  // 处理查看详情
  const handleViewDetail = (e, payment) => {
    e.stopPropagation();
    if (onPaymentClick) {
      onPaymentClick(payment);
    }
  };

  // 统计数据
  const stats = useMemo(() => ({
    total: payments.length,
    approving: payments.filter(p => p.status === '待审批' || p.status === '审批中').length,
    pending: payments.filter(p => p.status === '待付款').length,
    completed: payments.filter(p => p.status === '已付款').length,
    totalAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    totalRequiredAmount: payments.reduce((sum, p) => sum + (p.requiredAmount || 0), 0),
    totalPaidAmount: payments.reduce((sum, p) => sum + (p.paidAmount || 0), 0),
  }), [payments]);

  // 筛选后的付款单
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      // 搜索过滤
      const searchMatch = !searchTerm ||
        payment.docNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.supplierName?.includes(searchTerm);

      // 状态过滤
      const statusMatch = statusFilter === 'all' || payment.status === statusFilter;

      // 供应商过滤
      const supplierMatch = supplierFilter === 'all' || payment.supplierName === supplierFilter;

      // 付款时间过滤（日期范围）
      let dateMatch = true;
      if ((startDate || endDate) && payment.paymentTime) {
        const paymentDate = new Date(payment.paymentTime);
        paymentDate.setHours(0, 0, 0, 0);

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          dateMatch = dateMatch && paymentDate >= start;
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          dateMatch = dateMatch && paymentDate <= end;
        }
      } else if ((startDate || endDate) && !payment.paymentTime) {
        dateMatch = false;
      }

      return searchMatch && statusMatch && supplierMatch && dateMatch;
    });
  }, [payments, searchTerm, statusFilter, supplierFilter, startDate, endDate]);

  // 处理撤回操作
  const handleWithdraw = (e, payment) => {
    e.stopPropagation();
    if (onWithdraw) {
      onWithdraw(payment);
    } else {
      alert('已触发撤回操作');
    }
  };

  // 处理付款操作
  const handlePay = (e, payment) => {
    e.stopPropagation();
    if (onPay) {
      onPay(payment);
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">付款单管理</h2>
          <p className="text-sm text-slate-500 mt-1">管理所有付款单据，跟踪付款状态</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          <Plus size={18} />
          新增付款单
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">全部付款单</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <CreditCard size={20} className="text-slate-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">待审批</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.approving}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Clock size={20} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">待付款</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已付款</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.completed}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已支付总金额</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">¥{stats.totalPaidAmount.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign size={20} className="text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* 搜索框 */}
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="搜索付款单号、供应商..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 供应商筛选 */}
          <div className="relative min-w-[140px]">
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="w-full appearance-none px-4 py-2 pr-10 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              <option value="all">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          {/* 状态筛选 */}
          <div className="relative min-w-[120px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full appearance-none px-4 py-2 pr-10 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              <option value="all">全部状态</option>
              <option value="待审批">待审批</option>
              <option value="待付款">待付款</option>
              <option value="已付款">已付款</option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          {/* 付款时间筛选 - 日期范围 */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                placeholder="开始日期"
              />
            </div>
            <span className="text-slate-400">至</span>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                placeholder="结束日期"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
              >
                清除
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 付款单列表 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-4 font-medium text-slate-500">付款单号</th>
                <th className="p-4 font-medium text-slate-500">供应商</th>
                <th className="p-4 font-medium text-slate-500">关联发票单</th>
                <th className="p-4 font-medium text-slate-500">收款银行</th>
                <th className="p-4 font-medium text-slate-500">收款账号</th>
                <th className="p-4 font-medium text-slate-500">申请日期</th>
                <th className="p-4 font-medium text-slate-500 text-right">需支付金额</th>
                <th className="p-4 font-medium text-slate-500 text-right">已支付金额</th>
                <th className="p-4 font-medium text-slate-500">付款时间</th>
                <th className="p-4 font-medium text-slate-500">状态</th>
                <th className="p-4 font-medium text-slate-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <CreditCard size={40} className="text-slate-300" />
                      <p>暂无付款单数据</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map(payment => {
                    // 获取关联发票单号数组
                    const invoiceNos = payment.invoiceNos || [];
                    const invoiceCount = invoiceNos.length;
                    const isExpanded = expandedInvoiceIds.includes(payment.id);

                    return (
                      <React.Fragment key={payment.id}>
                        <tr className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-slate-600">{payment.docNo}</span>
                              {payment.isMerged && (
                                <span className="bg-purple-50 text-purple-600 text-[10px] px-1.5 py-0.5 rounded">
                                  合并
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 font-medium text-slate-800">{payment.supplierName}</td>
                          <td className="p-4">
                            {invoiceCount === 0 ? (
                              <span className="text-xs text-slate-400">-</span>
                            ) : invoiceCount === 1 ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onInvoiceClick?.(invoiceNos[0]);
                                }}
                                className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                {invoiceNos[0]}
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onInvoiceClick?.(invoiceNos[0]);
                                  }}
                                  className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {invoiceNos[0]}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInvoiceExpand(payment.id);
                                  }}
                                  className="flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600"
                                >
                                  <span>+{invoiceCount - 1}</span>
                                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-slate-600">{payment.bankName || '-'}</td>
                    <td className="p-4 text-slate-600 font-mono text-xs">{payment.bankAccount || '-'}</td>
                    <td className="p-4 text-slate-600">{payment.applyDate || '-'}</td>
                    <td className="p-4 text-right">
                      <span className="font-bold text-slate-800">¥{payment.requiredAmount?.toLocaleString()}</span>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-bold ${payment.paidAmount > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        ¥{payment.paidAmount?.toLocaleString()}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">{payment.paymentTime || '-'}</td>
                    <td className="p-4">
                      <StatusBadge status={payment.status} />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {/* 详情按钮 - 所有状态都显示 */}
                        <button
                          onClick={(e) => handleViewDetail(e, payment)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                        >
                          <Eye size={14} />
                          详情
                        </button>
                        {/* 待审批：显示撤回操作 */}
                        {(payment.status === '审批中' || payment.status === '待审批') && (
                          <button
                            onClick={(e) => handleWithdraw(e, payment)}
                            className="flex items-center gap-1 text-slate-500 hover:text-slate-700 text-sm"
                          >
                            <CornerUpLeft size={14} />
                            撤回
                          </button>
                        )}
                        {/* 待付款：显示付款操作 */}
                        {payment.status === '待付款' && (
                          <button
                            onClick={(e) => handlePay(e, payment)}
                            className="flex items-center gap-1 text-amber-600 hover:text-amber-700 text-sm"
                          >
                            <DollarSign size={14} />
                            付款
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* 展开的发票单列表 */}
                  {isExpanded && invoiceCount > 1 && (
                    <tr className="bg-slate-50/50">
                      <td colSpan="11" className="p-0">
                        <div className="px-12 py-2 flex flex-wrap gap-2">
                          {invoiceNos.map((invoiceNo, idx) => (
                            <button
                              key={invoiceNo}
                              onClick={(e) => {
                                e.stopPropagation();
                                onInvoiceClick?.(invoiceNo);
                              }}
                              className="inline-flex items-center px-2 py-1 bg-white border border-slate-200 rounded text-xs font-mono text-blue-600 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                            >
                              {idx === 0 && <span className="text-green-500 mr-1">●</span>}
                              {invoiceNo}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 底部汇总 */}
        {filteredPayments.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>共 {filteredPayments.length} 条付款单</span>
              <div className="flex gap-6">
                <span>
                  需支付总额：<span className="font-bold text-slate-800">¥{filteredPayments.reduce((sum, p) => sum + (p.requiredAmount || 0), 0).toLocaleString()}</span>
                </span>
                <span>
                  已支付总额：<span className="font-bold text-green-600">¥{filteredPayments.reduce((sum, p) => sum + (p.paidAmount || 0), 0).toLocaleString()}</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 新增付款单弹窗 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            {/* 弹窗标题 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">新增付款单</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>

            {/* 弹窗内容 */}
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">请选择付款单来源类型：</p>
              <div className="space-y-3">
                {/* 根据采购发票 */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    alert('请先生成结算单和发票单并完成开票后，再进行付款操作。');
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">根据采购发票</p>
                    <p className="text-xs text-slate-500">从已开票的发票单生成付款单</p>
                  </div>
                </button>

                {/* 根据采购订单 */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    alert('当前暂不支持此功能，敬请期待。');
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <ClipboardList size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">根据采购订单</p>
                    <p className="text-xs text-slate-500">从采购订单直接生成付款单</p>
                  </div>
                </button>

                {/* 根据入库单 */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    alert('当前暂不支持此功能，敬请期待。');
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <FileCheck size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">根据入库单</p>
                    <p className="text-xs text-slate-500">从入库单生成付款单</p>
                  </div>
                </button>

                {/* 根据供应商磅单 */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    alert('当前暂不支持此功能，敬请期待。');
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                    <Scale size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">根据供应商磅单</p>
                    <p className="text-xs text-slate-500">从供应商磅单生成付款单</p>
                  </div>
                </button>

                {/* 代扣代缴 */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    alert('当前暂不支持此功能，敬请期待。');
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                    <DollarSign size={20} className="text-rose-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">代扣代缴</p>
                    <p className="text-xs text-slate-500">创建代扣代缴付款单</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentView;
